import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import {
  syncCanvasState,
  addNode,
  deleteNode,
  addEdgeAction,
  clearCanvas,
  undo,
  redo,
} from './canvas/canvasSlice'
import { resetPipelineState } from './pipelineSlice'
import type { ValidationResult } from '../utils/pipelineValidator'

export interface ValidationState {
  result: ValidationResult | null
  isValidating: boolean
  /**
   * `true` once the canvas (or any node config) has changed since the last
   * successful validate. Run is gated on `!isDirty` so users always validate
   * after edits.
   */
  isDirty: boolean
  lastValidatedAt: number | null
  /** Whether the user has the issues panel open. */
  isPanelOpen: boolean
}

const initialState: ValidationState = {
  result: null,
  isValidating: false,
  isDirty: false,
  lastValidatedAt: null,
  isPanelOpen: false,
}

const validationSlice = createSlice({
  name: 'validation',
  initialState,
  reducers: {
    startValidating(state) {
      state.isValidating = true
    },
    setValidationResult(state, action: PayloadAction<ValidationResult>) {
      state.result = action.payload
      state.isValidating = false
      state.isDirty = false
      state.lastValidatedAt = Date.now()
      state.isPanelOpen = !action.payload.isValid
    },
    markValidationDirty(state) {
      if (state.result) state.isDirty = true
    },
    setPanelOpen(state, action: PayloadAction<boolean>) {
      state.isPanelOpen = action.payload
    },
    dismissValidation(state) {
      state.result = null
      state.isPanelOpen = false
      state.isDirty = false
      state.lastValidatedAt = null
    },
  },
  extraReducers: builder => {
    const dirty = (state: ValidationState) => {
      if (state.result) state.isDirty = true
    }
    builder
      .addCase(syncCanvasState, dirty)
      .addCase(addNode, dirty)
      .addCase(deleteNode, dirty)
      .addCase(addEdgeAction, dirty)
      .addCase(clearCanvas, () => initialState)
      .addCase(undo, dirty)
      .addCase(redo, dirty)
      .addCase(resetPipelineState, () => initialState)
  },
})

export const {
  startValidating,
  setValidationResult,
  markValidationDirty,
  setPanelOpen,
  dismissValidation,
} = validationSlice.actions

export default validationSlice.reducer
